/**
 * The night-sea SVG art: island ports, the home-harbor lighthouse, the ship,
 * and the chart décor (stars, moon, waves, compass rose). Everything is drawn
 * in map units inside the shared 1000×620 viewBox, so the same builders serve
 * the live board and the reveal's mini charts (which just render smaller).
 */

const NS = "http://www.w3.org/2000/svg";

/** Create a namespaced SVG element with attributes. */
export function svgEl<K extends keyof SVGElementTagNameMap>(
  tag: K,
  attrs: Record<string, string | number> = {},
): SVGElementTagNameMap[K] {
  const el = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) el.setAttribute(k, String(v));
  return el;
}

/** Group helper: an SVG <g> with a class and optional transform. */
function group(cls: string, transform?: string): SVGGElement {
  const g = svgEl("g", { class: cls });
  if (transform) g.setAttribute("transform", transform);
  return g;
}

/* ---------- Island ports ---------- */

/** An island port at the origin: two mounds, a little mast with a pennant,
 *  a warm lamp, and a visit-order badge (hidden until visited). `artScale`
 *  enlarges the artwork (not the tap target) — used by the portrait chart. */
export function makePort(idx: number, x: number, y: number, interactive: boolean, artScale = 1): SVGGElement {
  const g = group("port", `translate(${x} ${y})`);
  g.dataset.idx = String(idx);

  if (interactive) g.appendChild(svgEl("circle", { class: "hit", r: 46 }));
  g.appendChild(svgEl("circle", { class: "halo", r: 24 * artScale }));

  // mirrored / slightly rescaled silhouettes give cheap organic variety
  const flip = idx % 2 === 1 ? -1 : 1;
  const s = (0.92 + (idx % 3) * 0.08) * flip * artScale;
  const isle = group("isle-wrap", `scale(${s} ${Math.abs(s)})`);
  isle.appendChild(svgEl("ellipse", { class: "isle-shadow", cx: 0, cy: 19, rx: 34, ry: 7 }));
  isle.appendChild(svgEl("path", { class: "isle-back", d: "M-24,16 Q-8,-14 10,-7 Q26,-1 29,16 Z" }));
  isle.appendChild(svgEl("path", { class: "isle", d: "M-32,17 Q-18,-3 0,-8 Q20,-12 30,17 Z" }));
  isle.appendChild(svgEl("line", { class: "mast", x1: 8, y1: -6, x2: 8, y2: -26 }));
  isle.appendChild(svgEl("path", { class: "flag", d: "M8,-26 L20,-22 L8,-18 Z" }));
  isle.appendChild(svgEl("circle", { class: "lamp-glow", cx: 8, cy: -6, r: 9 }));
  isle.appendChild(svgEl("circle", { class: "lamp", cx: 8, cy: -6, r: 3 }));
  g.appendChild(isle);

  const badge = group("badge", `translate(0 ${-40 * artScale}) scale(${artScale})`);
  badge.appendChild(svgEl("circle", { class: "badge-ring", r: 12 }));
  const t = svgEl("text", { class: "badge-num", y: 4.5, "text-anchor": "middle" });
  badge.appendChild(t);
  g.appendChild(badge);
  return g;
}

/** The home harbor at the origin: a wide isle with a lighthouse and beams. */
export function makeHarbor(x: number, y: number, interactive: boolean, artScale = 1): SVGGElement {
  const g = group("port harbor", `translate(${x} ${y})`);
  g.dataset.idx = "0";

  if (interactive) g.appendChild(svgEl("circle", { class: "hit", r: 52 }));
  const art = group("harbor-art", `scale(${artScale})`);
  art.appendChild(svgEl("ellipse", { class: "isle-shadow", cx: 0, cy: 21, rx: 44, ry: 8 }));
  art.appendChild(svgEl("path", { class: "isle-back", d: "M-30,18 Q-14,-8 6,-4 Q28,0 34,18 Z" }));
  art.appendChild(svgEl("path", { class: "isle", d: "M-44,19 Q-26,1 -4,-5 Q24,-10 42,19 Z" }));

  // the lighthouse: tapered tower, two dark bands, gallery, dome, light
  art.appendChild(svgEl("path", { class: "lh-tower", d: "M-7,-4 L7,-4 L4.5,-34 L-4.5,-34 Z" }));
  art.appendChild(svgEl("path", { class: "lh-band", d: "M-6.4,-11 L6.4,-11 L5.9,-17 L-5.9,-17 Z" }));
  art.appendChild(svgEl("path", { class: "lh-band", d: "M-5.5,-22 L5.5,-22 L5.1,-27 L-5.1,-27 Z" }));
  art.appendChild(svgEl("rect", { class: "lh-gallery", x: -6, y: -37, width: 12, height: 3.4, rx: 1.4 }));
  art.appendChild(svgEl("path", { class: "lh-dome", d: "M-4.5,-37 Q0,-46 4.5,-37 Z" }));
  art.appendChild(svgEl("path", { class: "beam beam-r", d: "M4,-40 L86,-58 L86,-24 Z" }));
  art.appendChild(svgEl("path", { class: "beam beam-l", d: "M-4,-40 L-86,-58 L-86,-24 Z" }));
  art.appendChild(svgEl("circle", { class: "lamp-glow", cx: 0, cy: -39.5, r: 10 }));
  art.appendChild(svgEl("circle", { class: "lamp", cx: 0, cy: -39.5, r: 3.4 }));

  const label = svgEl("text", { class: "port-label", y: 38, "text-anchor": "middle" });
  label.textContent = "HARBOR";
  art.appendChild(label);
  g.appendChild(art);
  return g;
}

/* ---------- The ship ---------- */

/** A little sloop, drawn facing east (rotate the group to steer it). */
export function makeShip(artScale = 1): SVGGElement {
  const g = group("ship");
  const scaled = group("ship-scale", `scale(${artScale})`);
  const body = group("ship-body");
  body.appendChild(svgEl("path", { class: "hull", d: "M-14,3 Q0,11 14,3 L9.5,-1.5 L-9.5,-1.5 Z" }));
  body.appendChild(svgEl("path", { class: "sail-main", d: "M1.5,-3 L1.5,-21 Q13,-10 3.5,-3 Z" }));
  body.appendChild(svgEl("path", { class: "sail-jib", d: "M-1,-4 L-1,-17 Q-10,-9 -3.5,-4 Z" }));
  body.appendChild(svgEl("circle", { class: "ship-lamp", cx: 0.5, cy: -1.8, r: 1.8 }));
  scaled.appendChild(body);
  g.appendChild(scaled);
  return g;
}

/* ---------- Chart décor ---------- */

/** Tiny deterministic PRNG so the star field is identical on every render. */
function mulberry(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** The sky/sea backdrop: stars, the moon (top-right), faint wave squiggles,
 *  and a small compass rose (bottom-right). Purely decorative. */
export function makeDecor(w: number, h: number): SVGGElement {
  const g = group("decor");
  const rnd = mulberry(20260610);

  // stars in the upper sky
  for (let i = 0; i < 46; i++) {
    const x = rnd() * w;
    const y = rnd() * h * 0.5;
    const r = 0.6 + rnd() * 1.3;
    const star = svgEl("circle", { class: "star", cx: x.toFixed(1), cy: y.toFixed(1), r: r.toFixed(2) });
    star.style.opacity = (0.25 + rnd() * 0.55).toFixed(2);
    if (rnd() < 0.3) star.classList.add("twinkle");
    g.appendChild(star);
  }

  // the moon, tucked into the top-right corner (no map point sits there)
  g.appendChild(svgEl("circle", { class: "moon-glow", cx: w - 46, cy: 44, r: 64 }));
  g.appendChild(svgEl("circle", { class: "moon", cx: w - 46, cy: 44, r: 23 }));
  g.appendChild(svgEl("path", { class: "moon-crater", d: `M${w - 54},38 a4,4 0 1,0 8,0 a4,4 0 1,0 -8,0` }));

  // faint wave squiggles scattered over open water
  for (let i = 0; i < 14; i++) {
    const x = 40 + rnd() * (w - 120);
    const y = h * 0.22 + rnd() * (h * 0.7);
    const wv = svgEl("path", {
      class: "wave",
      d: `M${x.toFixed(0)},${y.toFixed(0)} q7,-5 14,0 q7,5 14,0`,
    });
    wv.style.opacity = (0.10 + rnd() * 0.12).toFixed(2);
    g.appendChild(wv);
  }

  // compass rose, bottom-right
  const compass = group("compass", `translate(${w - 62} ${h - 46})`);
  compass.appendChild(svgEl("circle", { class: "compass-ring", r: 24 }));
  compass.appendChild(svgEl("path", { class: "compass-star", d: "M0,-20 L4,-4 L20,0 L4,4 L0,20 L-4,4 L-20,0 L-4,-4 Z" }));
  compass.appendChild(svgEl("path", { class: "compass-star-minor", d: "M0,-9 L9,0 L0,9 L-9,0 Z" }));
  const n = svgEl("text", { class: "compass-n", y: -28, "text-anchor": "middle" });
  n.textContent = "N";
  compass.appendChild(n);
  g.appendChild(compass);

  return g;
}
